import React from "react";
import {
  createColumn,
  formatAggregatedFooter,
  formatNumericCell,
  formatPriceCell,
  getCommonBodyCellProps,
  getCommonBodyCellPropsTradesPositions,
  getCommonFooterCellProps,
  getCommonHeadCellProps,
} from "./commonUtils";
import {
  formatCurrency,
  formatNumber,
  formatPosition,
} from "../../../utils/dataCalculations";

export const SECURITY_TYPE_LABELS = {
  CASH: "Cash",
  "BOND-CORP": "Corporate Bond",
  "BOND-GOVT": "Government Bond",
  "BOND-CONV": "Convertible Bond",
  "BOND-TRS": "Bond Total Return Swap",
  "BOND-CVT-SWAP": "Convertible Bond Swap",
  BANKDEBT: "Bank Debt",
  "TRS-BANKDEBT": "Bank Debt Total Return Swap",
  "EQ-DOMESTIC": "Domestic Equity",
  "EQ-INTL": "International Equity",
  "EQ-DOM-SWAP": "Domestic Equity Swap",
  "EQ-INTL-SWAP": "International Equity Swap",
  "EQ-PREF": "Preferred Equity",
  "EQ-PREF-SWAP": "Preferred Equity Swap",
  "EQ-CL": "Call Option",
  "EQ-PT": "Put Option",
  WARRANT: "Warrant",
  RIGHT: "Right",
  IDX: "Index",
  "IDX-SWAP": "Index Swap",
  CDS: "Credit Default Swap",
  CDX: "CDX (Credit Default Swap Index)",
  IRS: "Interest Rate Swap",
  "": "Undefined",
  // TODO: confirm and add friendly labels for "EQ-MINOR" and
  // "EQ-INTL-SWP-MNR" once confirmed with a domain expert. Until then they
  // fall through to the raw-code default below.
};

export const getSecurityTypeLabel = (code) =>
  Object.prototype.hasOwnProperty.call(SECURITY_TYPE_LABELS, code)
    ? SECURITY_TYPE_LABELS[code]
    : code;

export const columns = (theme, globalFilter, formatCurrency, formatNumber) => {
  const highlightMatch = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    const textStr = String(text);
    const searchStr = String(searchTerm);
    const regex = new RegExp(`(${searchStr})`, "gi");
    const parts = textStr.split(regex);
    return parts?.map((part, index) =>
      part.toLowerCase() === searchStr.toLowerCase() ? (
        <span
          key={index}
          style={{
            backgroundColor: theme.palette.warning.light,
            color: theme.palette.warning.contrastText,
            padding: "1px 2px",
            borderRadius: theme.shape.borderRadius * 0.5,
          }}
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const commonHeadCellProps = getCommonHeadCellProps(theme);
  const commonBodyCellProps = getCommonBodyCellPropsTradesPositions(theme);
  const commonFooterCellProps = getCommonFooterCellProps(theme);

  return [
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "StrategyType",
      "Strategy Type",
      120,
      "left",
      {
        other: {
          Cell: ({ cell, table }) =>
            highlightMatch(cell.getValue(), table.options.state.globalFilter),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "StrategyName",
      "Strategy Name",
      160,
      "left",
      {
        body: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        other: {
          Cell: ({ cell, table }) => {
            const value = cell.getValue();
            return value && value !== "nan"
              ? highlightMatch(value, table.options.state.globalFilter)
              : "";
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "security_name",
      "Security Name",
      170,
      "left",
      {
        body: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        other: {
          Cell: ({ cell, table }) => {
            const value = cell.getValue();
            return value && value !== "nan"
              ? highlightMatch(value, table.options.state.globalFilter)
              : "-";
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "Security_Type",
      "Security Type",
      120,
      "left",
      {
        other: {
          Cell: ({ cell, table }) => {
            const value = cell.getValue();
            return value && value !== "nan"
              ? highlightMatch(
                  getSecurityTypeLabel(value),
                  table.options.state.globalFilter
                )
              : "-";
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "trading_pnl",
      "Trading P&L",
      120,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "trading_pnl",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "current_price",
      "Current Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => {
            const value = cell.getValue();
            return value
              ? new Intl.NumberFormat("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 4,
                }).format(value)
              : "-";
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "realtime_quantity",
      "Real-time Qty",
      120,
      "right",
      {
        head: { borderLeft: `2px solid ${theme.palette.grey[900]}` },
        body: { borderLeft: `2px solid ${theme.palette.grey[900]}` },
        footer: { borderLeft: `2px solid ${theme.palette.grey[900]}` },
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "net_trading_quantity",
      "Net Trade Today",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sod_quantity",
      "SOD Quantity",
      120,
      "right",
      {
        other: {
          sortingFn: (rowA, rowB, columnId) => {
            const aValue = rowA.getValue(columnId);
            const bValue = rowB.getValue(columnId);
            if ((!aValue || aValue === "nan") && bValue && bValue !== "nan")
              return 1;
            if (aValue && aValue !== "nan" && (!bValue || bValue === "nan"))
              return -1;
            const a = !aValue || aValue === "nan" ? 0 : aValue;
            const b = !bValue || bValue === "nan" ? 0 : bValue;
            return a - b;
          },
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "buy_trade_quantity",
      "Buy Qty",
      120,
      "right",
      {
        head: {
          borderLeft: `2px solid ${theme.palette.grey[900]}`,
        },
        body: {
          borderLeft: `2px solid ${theme.palette.grey[900]}`,
        },
        footer: {
          borderLeft: `2px solid ${theme.palette.grey[900]}`,
        },
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "buy_trade_price",
      "Buy Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 4),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "buy_trade_MV",
      "Buy MV",
      120,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "buy_trade_MV",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sell_trade_quantity",
      "Sell Qty",
      120,
      "right",
      {
        head: {
          borderLeft: `2px solid ${theme.palette.grey[900]}`,
        },
        body: {
          borderLeft: `2px solid ${theme.palette.grey[900]}`,
        },
        footer: {
          borderLeft: `2px solid ${theme.palette.grey[900]}`,
        },
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sell_trade_price",
      "Sell Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sell_trade_MV",
      "Sell MV",
      120,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "sell_trade_MV",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "short_trade_quantity",
      "Short Qty",
      120,
      "right",
      {
        head: {
          borderLeft: `2px solid ${theme.palette.grey[900]}`,
        },
        body: {
          borderLeft: `2px solid ${theme.palette.grey[900]}`,
        },
        footer: {
          borderLeft: `2px solid ${theme.palette.grey[900]}`,
        },
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "short_trade_price",
      "Short Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "short_trade_MV",
      "Short MV",
      120,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "short_trade_MV",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "cover_trade_quantity",
      "Cover Qty",
      120,
      "right",
      {
        head: { borderLeft: `2px solid ${theme.palette.grey[900]}` },
        body: { borderLeft: `2px solid ${theme.palette.grey[900]}` },
        footer: { borderLeft: `2px solid ${theme.palette.grey[900]}` },
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "cover_trade_price",
      "Cover Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 4),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "cover_trade_MV",
      "Cover MV",
      120,
      "right",
      {
        head: {
          borderRight: `2px solid ${theme.palette.divider}`,
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
        },
        body: {
          borderRight: `2px solid ${theme.palette.grey[300]}`,
        },
        footer: {
          borderRight: `2px solid ${theme.palette.grey[300]}`,
        },
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "cover_trade_MV",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "settle_currency",
      "Settle Currency",
      130,
      "left",
      {
        other: {
          Cell: ({ cell, table }) => {
            const value = cell.getValue();
            return value && value !== "nan"
              ? highlightMatch(value, table.options.state.globalFilter)
              : "-";
          },
        },
      }
    ),
  ];
};

export const getTradeColumnsEthan = (theme) => {
  const commonHeadCellProps = getCommonHeadCellProps(theme);
  const commonBodyCellProps = getCommonBodyCellProps(theme);
  const commonFooterCellProps = getCommonFooterCellProps(theme);

  return [
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "security_name",
      "Security Name",
      100,
      "left",
      {
        body: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        other: {
          Cell: ({ cell }) => cell.getValue() || "-",
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "buy_trade_quantity",
      "Buy Qty",
      100,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.text.primary
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sell_trade_quantity",
      "Sell Qty",
      100,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.text.primary
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "trading_pnl",
      "Trading P&L",
      100,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "trading_pnl",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "current_price",
      "Current Price",
      100,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 4),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "buy_trade_price",
      "Buy Price",
      100,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 2),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "buy_trade_MV",
      "Buy MV",
      120,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "buy_trade_MV",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sell_trade_price",
      "Sell Price",
      100,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 2),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sell_trade_MV",
      "Sell MV",
      100,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "sell_trade_MV",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "short_trade_quantity",
      "Short Qty",
      100,
      "right",
      {
        head: { borderLeft: `2px solid ${theme.palette.grey[900]}` },
        body: { borderLeft: `2px solid ${theme.palette.grey[900]}` },
        footer: { borderLeft: `2px solid ${theme.palette.grey[900]}` },
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.text.primary
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "short_trade_price",
      "Short Price",
      100,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 2),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "short_trade_MV",
      "Short MV",
      100,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "short_trade_MV",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "cover_trade_quantity",
      "Cover Qty",
      100,
      "right",
      {
        head: { borderLeft: `2px solid ${theme.palette.grey[900]}` },
        body: { borderLeft: `2px solid ${theme.palette.grey[900]}` },
        footer: { borderLeft: `2px solid ${theme.palette.grey[900]}` },
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.text.primary
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "cover_trade_price",
      "Cover Price",
      100,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 2),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "cover_trade_MV",
      "Cover MV",
      100,
      "right",
      {
        head: { borderRight: `2px solid ${theme.palette.divider}` },
        body: { borderRight: `2px solid ${theme.palette.divider}` },
        footer: { borderRight: `2px solid ${theme.palette.divider}` },
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "cover_trade_MV",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
  ];
};
